import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import toast, { Toaster } from "react-hot-toast";
import { FiCode, FiEye, FiX, FiExternalLink } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const DetailRow = ({ label, value, link, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
    {link && value ? (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-[#999900] hover:underline break-all flex items-center gap-1"
      >
        {value} <FiExternalLink size={11} />
      </a>
    ) : (
      <div className="text-sm text-gray-800 break-words whitespace-pre-wrap">
        {value ?? <span className="text-gray-300">—</span>}
      </div>
    )}
  </div>
);

const DeveloperCollaboration = () => {
  const [submissions, setSubmissions]           = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailLoading, setDetailLoading]       = useState(false);
  const [showModal, setShowModal]               = useState(false);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/devloper-collaboration/submissions`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch submissions");
      const result = await res.json();
      setSubmissions(result.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const fetchSubmissionDetails = async (id) => {
    setDetailLoading(true);
    setShowModal(true);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${baseUrl}/devloper-collaboration/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch submission details");
      const result = await res.json();
      setSelectedSubmission(result.data);
    } catch {
      toast.error("Failed to load submission details");
      setShowModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const closeModal = () => { setShowModal(false); setSelectedSubmission(null); };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <FiCode size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Developer Collaboration</h2>
              <p className="text-sm text-gray-400 mt-0.5">Manage developer collaboration submissions</p>
            </div>
            {!loading && submissions.length > 0 && (
              <span className="ml-auto text-xs bg-gray-200 text-gray-600 font-semibold px-2.5 py-1 rounded-full">
                {submissions.length} submission{submissions.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Main card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-7 h-7 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
            ) : submissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiCode size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No submissions found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">#</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3 hidden sm:table-cell">Email</th>
                      <th className="px-5 py-3">Collaboration Type</th>
                      <th className="px-5 py-3 hidden md:table-cell">Project Title</th>
                      <th className="px-5 py-3 hidden xl:table-cell">Created</th>
                      <th className="px-5 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {submissions.map((s, index) => (
                      <tr key={s.id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-3.5 text-gray-400 text-xs">{index + 1}</td>
                        <td className="px-5 py-3.5 font-medium text-gray-800">{s.name || "—"}</td>
                        <td className="px-5 py-3.5 hidden sm:table-cell text-gray-500">{s.email || "—"}</td>
                        <td className="px-5 py-3.5">
                          {s.collaborationType ? (
                            <span className="inline-block text-xs font-medium bg-indigo-50 text-indigo-600 px-2.5 py-0.5 rounded-full">
                              {s.collaborationType}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell max-w-[200px]">
                          <span className="block truncate text-gray-700">{s.projectTitle || "—"}</span>
                        </td>
                        <td className="px-5 py-3.5 hidden xl:table-cell text-xs text-gray-400">
                          {formatDate(s.createdAt)}
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => fetchSubmissionDetails(s.id)}
                            title="View Details"
                            className="p-1.5 rounded-lg bg-[#ffffe0] text-[#999900] hover:bg-[#ffffcc] transition mx-auto block"
                          >
                            <FiEye size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-800">Submission Details</h3>
              <button
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                <FiX size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6">
              {detailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-7 h-7 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : selectedSubmission ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <DetailRow label="ID"                 value={selectedSubmission.id} />
                  <DetailRow label="Name"               value={selectedSubmission.name} />
                  <DetailRow label="Email"              value={selectedSubmission.email} />
                  <DetailRow label="Phone Number"       value={selectedSubmission.phoneNumber} />
                  <DetailRow label="Country"            value={selectedSubmission.country} />
                  <div>
                    <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Collaboration Type</div>
                    {selectedSubmission.collaborationType ? (
                      <span className="inline-block text-xs font-medium bg-indigo-50 text-indigo-600 px-2.5 py-0.5 rounded-full">
                        {selectedSubmission.collaborationType}
                      </span>
                    ) : <span className="text-gray-300 text-sm">—</span>}
                  </div>
                  <DetailRow label="Website"   value={selectedSubmission.website} link />
                  <DetailRow label="App URL"   value={selectedSubmission.appUrl}  link />
                  <DetailRow label="Created"   value={formatDate(selectedSubmission.createdAt)} />
                  <DetailRow label="Updated"   value={formatDate(selectedSubmission.updatedAt)} />
                  <DetailRow label="Project Title"       value={selectedSubmission.projectTitle}       wide />
                  <DetailRow label="Project Description" value={selectedSubmission.projectDescription} wide />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <FiCode size={28} className="text-gray-300 mb-2" />
                  <p className="text-sm">No details available.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end rounded-b-xl flex-shrink-0">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperCollaboration;
